'use client'

import type { FC } from 'react'
import IconSelector from 'components/AdminComponents/Config/IconSelector'
import { IconDelete } from 'components/icons'
import SelectMenu from 'components/SelectMenu'
import { Button } from 'components/ui/button'
import { Input } from 'ui/input'
import { Label } from 'ui/label'
import FieldWrapper from './FieldWrapper'
import type { FieldRendererProps } from './types'

export const buttonArrayFieldConfig = {
    getInitialValue: (field: TFormField) => field.defaultValue ?? [],
    getNewArrayItem: () => ({ text: '', link: '', variant: 'default', icon: '' }),
    extraPropsKind: 'array' as const
} satisfies Partial<import('./types').FieldTypeConfig>

const ButtonArrayField: FC<FieldRendererProps> = ({
    field,
    value,
    error,
    formState,
    onArrayChange,
    onAddArrayItem,
    onRemoveArrayItem
}) => {
    const items = (value || []) as { text?: string; link?: string; variant?: string; icon?: string }[]
    const arr = (formState?.[field.name] ?? items) as unknown[]

    return (
        <FieldWrapper fieldName={field.name} error={error}>
            <div className='space-y-4'>
                <Label>{field.label}</Label>
                {items.map((item: typeof items[0], index: number) => (
                    <div key={index} className='p-4 border border-border rounded-lg bg-card'>
                        <div className='flex flex-col space-y-3'>
                            <div className='flex justify-between items-center'>
                                <h4 className='text-sm font-medium'>Button {index + 1}</h4>
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    type='button'
                                    onClick={() => onRemoveArrayItem?.(field.name, index, field)}
                                    disabled={!!field.min && arr.length <= field.min}
                                    className='h-8 w-8'
                                >
                                    <IconDelete />
                                </Button>
                            </div>
                            <div className='space-y-2'>
                                <Label className='text-xs'>Text</Label>
                                <Input
                                    placeholder='Text'
                                    value={item?.text ?? ''}
                                    onChange={e =>
                                        onArrayChange?.(field.name, index, field, {
                                            ...item,
                                            text: e.target.value
                                        })
                                    }
                                />
                            </div>
                            <div className='space-y-2'>
                                <IconSelector
                                    label='Icon (optional)'
                                    value={item?.icon ?? ''}
                                    onChange={iconName =>
                                        onArrayChange?.(field.name, index, field, {
                                            ...item,
                                            icon: iconName
                                        })
                                    }
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label className='text-xs'>Link</Label>
                                <Input
                                    placeholder='Link'
                                    value={item?.link ?? ''}
                                    onChange={e =>
                                        onArrayChange?.(field.name, index, field, {
                                            ...item,
                                            link: e.target.value
                                        })
                                    }
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label className='text-xs'>Type</Label>
                                <SelectMenu
                                    name={`${field.name}[${index}].variant`}
                                    label='Type'
                                    items={[
                                        { id: 'default', name: 'Default' },
                                        { id: 'destructive', name: 'Destructive' },
                                        { id: 'outline', name: 'Outline' },
                                        { id: 'secondary', name: 'Secondary' },
                                        { id: 'ghost', name: 'Ghost' },
                                        { id: 'link', name: 'Link' }
                                    ]}
                                    value={item?.variant ?? 'default'}
                                    set={v =>
                                        onArrayChange?.(field.name, index, field, {
                                            ...item,
                                            variant: v
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                ))}
                {field?.max && field.max > 1 && (
                    <div className='flex items-items mt-4 gap-2 flex-col'>
                        <Button
                            type='button'
                            onClick={() => onAddArrayItem?.(field.name, field)}
                            disabled={!!field.max && arr.length >= field.max}
                        >
                            Add Button
                        </Button>
                        <span>
                            {!!field.min && arr.length < field.min && (
                                <p className='text-destructive text-sm'>
                                    Minimum {field.min} buttons required
                                </p>
                            )}
                            {field.max && arr.length >= field.max && (
                                <p className='text-warning text-sm'>
                                    Maximum allowed buttons ({field.max}) reached
                                </p>
                            )}
                        </span>
                    </div>
                )}
            </div>
        </FieldWrapper>
    )
}

export default ButtonArrayField
